import {ChangeDetectionStrategy, Component, computed, effect, inject, input, model, OnDestroy, OnInit, Signal, signal, WritableSignal} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatOptionModule} from "@angular/material/core"
import {MatDialog} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {DropdownButtonComponent} from "@app/common/components/buttons/dropdown-button/dropdown-button.component"
import {RenameDialogComponent} from "@app/common/components/dialogs/rename-dialog/rename-dialog.component"
import {OrganizationsService} from "@app/common/services/organizations/organizations.service"
import {
    MaterialOutputFragment,
    MaterialOutputJobStateGQL,
    MaterialOutputsDataFragment,
    MaterialOutputsUpdateResultGQL,
} from "@app/platform/components/materials/material-outputs/material-outputs.generated"
import {MaterialOutputService} from "@app/platform/services/material/material-output.service"
import {BakeTextureService} from "@app/template-editor/services/bake-texture.service"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {PbrExport} from "@cm/material-maps-exporter"
import {DropdownInlineButtonComponent} from "@common/components/buttons/dropdown-inline-button/dropdown-inline-button.component"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {HintComponent} from "@common/components/hint/hint.component"
import {PlaceholderComponent} from "@common/components/placeholders/placeholder/placeholder.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {MaterialMapsExporterService, PbrExportSummary} from "@common/services/material-maps-exporter/material-maps-exporter.service"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {ContentTypeModel, JobState, MaterialOutputType} from "@generated"
import {
    MaterialOutputButtonComponent,
    OutputButtonProps,
    SharedOutputButtonProps,
} from "@platform/components/materials/material-output-button/material-output-button.component"
import {MaterialOutputCustomPbrExportFormComponent} from "@platform/components/materials/material-output-custom-pbr-export-form/material-output-custom-pbr-export-form.component"
import {MaterialOutputPbrExportDetailComponent} from "@platform/components/materials/material-output-pbr-export-detail/material-output-pbr-export-detail.component"
import {MaterialOutputsDataGQL} from "@platform/components/materials/material-outputs/material-outputs.generated"
import {catchError, of, Subject, switchMap, takeUntil, timer} from "rxjs"

type MaterialOutputWithState = MaterialOutputFragment & {state?: JobState | "Processing"}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-outputs",
    imports: [
        MatTooltipModule,
        RouterModule,
        MatButtonModule,
        DropdownButtonComponent,
        DropdownInlineButtonComponent,
        MaterialOutputButtonComponent,
        MatInputModule,
        MatOptionModule,
        FormsModule,
        HintComponent,
        PlaceholderComponent,
        DropdownInlineButtonComponent,
        MaterialOutputCustomPbrExportFormComponent,
        MaterialOutputPbrExportDetailComponent,
    ],
    providers: [SceneManagerService, MeshGeometryService, BakeTextureService, MaterialOutputService],
    templateUrl: "./material-outputs.component.html",
    styleUrl: "./material-outputs.component.scss",
})
export class MaterialOutputsComponent implements OnInit, OnDestroy {
    readonly $materialId = input.required<string>({alias: "materialId"})
    readonly $needsConfirmationToClose = model.required<boolean>({alias: "needsConfirmationToClose"})

    readonly $material = signal<MaterialOutputsDataFragment | undefined>(undefined)
    readonly $outputsWithState: WritableSignal<MaterialOutputWithState[]> = signal([])
    readonly $localOutputsInProgress: WritableSignal<{id: string; type: MaterialOutputType; outputCreated: boolean}[]> = signal([])

    readonly $customPbrExports = computed(() => this.$outputsWithState().filter((outputItem) => outputItem.type === MaterialOutputType.PbrExportCustom))

    // The global "delete MaterialMapsExport" ability is not organization-scoped, so on its own it would
    // expose the delete button to users from other organizations viewing a publicly downloadable material.
    // Also require delete permission on the material itself, which is organization-scoped.
    readonly $canDeletePbrExport = computed(() => {
        const material = this.$material()
        return !!material && this.$can().delete.materialMapsExport() && this.$can().delete.item(material)
    })

    // Generating/managing outputs (pCon, PBR exports) is org-scoped: a non-owner viewing a publicly
    // downloadable material can read outputs but must not trigger generation jobs or delete them on
    // someone else's material. Mirrors the PBR export gating (see the template).
    readonly $canGenerateOutputs = computed(() => {
        const material = this.$material()
        return !!material && this.$can().update.material(material, "outputs")
    })

    stopPollingJobs$ = new Subject<void>()

    readonly permission = inject(PermissionsService)
    readonly matDialog = inject(MatDialog)
    readonly materialMapsExporterService = inject(MaterialMapsExporterService)
    readonly materialOutputService = inject(MaterialOutputService)
    readonly notifications = inject(NotificationsService)
    readonly refresh = inject(RefreshService)
    readonly organizations = inject(OrganizationsService)
    protected readonly sceneManagerService = inject(SceneManagerService)

    $can = this.permission.$to

    readonly dataGql = inject(MaterialOutputsDataGQL)
    readonly materialOutputJobStateGql = inject(MaterialOutputJobStateGQL)
    readonly updateMaterialOutputResultGql = inject(MaterialOutputsUpdateResultGQL)

    constructor() {
        effect(() => {
            if (this.$localOutputsInProgress().length > 0) {
                this.$needsConfirmationToClose.update(() => true)
            } else {
                this.$needsConfirmationToClose.update(() => false)
            }
        })
    }

    ngOnInit() {
        void this.loadMaterialOutputs().then((material) => {
            this.refresh.observeItem$(material, false).subscribe(() => {
                void this.loadMaterialOutputs()
            })
        })

        this.sceneManagerService.$lodType.set("pathTraced")
        this.sceneManagerService.$computeDummyReifiedMeshData.set(true)
    }

    ngOnDestroy() {
        this.stopPollingJobs$.next()
        this.stopPollingJobs$.complete()
    }

    /**
     * Periodically checks the status of running output jobs.
     * Once the job is finished, the status is updated and the output is available for download.
     */
    async startJobStatusPolling(materialOutputs: MaterialOutputFragment[]) {
        this.stopPollingJobs$.next()
        materialOutputs.forEach((materialOutput) => {
            if (materialOutput.job && this.isJobRunning(materialOutput.job.state)) {
                timer(5000, 10 * 1000)
                    .pipe(
                        takeUntil(this.stopPollingJobs$),
                        switchMap(async () => {
                            const {job} = await fetchThrowingErrors(this.materialOutputJobStateGql)({id: materialOutput.job!.id})
                            if (!this.isJobRunning(job.state)) {
                                // this will reload the material outputs as well
                                this.refresh.item({id: this.$materialId(), __typename: ContentTypeModel.Material})
                            }
                        }),
                    )
                    .subscribe()
            }
        })
    }

    async loadMaterialOutputs() {
        const {material} = await fetchThrowingErrors(this.dataGql)({id: this.$materialId()})
        this.$material.set(material)

        const outputsWithState: MaterialOutputWithState[] = material.outputs.map((materialOutput) => {
            let outputState
            if (materialOutput.job?.state) {
                outputState = materialOutput.job?.state
            } else if (materialOutput.result) {
                // migrated existing outputs will have no job connected
                outputState = JobState.Complete
            }

            return {
                ...materialOutput,
                state: outputState,
            }
        })

        // remove local outputs that were created in the backend
        this.$localOutputsInProgress.update((outputs) => outputs.filter((output) => !output.outputCreated))
        const localOutputsInProgress: MaterialOutputWithState[] = this.$localOutputsInProgress()
            .filter((output) => !output.outputCreated)
            .map((output) => ({
                id: output.id,
                __typename: "MaterialOutput",
                type: output.type,
                state: "Processing",
            }))
        this.$outputsWithState.set(outputsWithState.concat(localOutputsInProgress))

        this.startJobStatusPolling(material.outputs ?? [])

        return material
    }

    generateCustomPbrExport = async (conversionRequest: PbrExport.Types.ConversionRequest) => {
        const exportName = this.$material()?.name ?? ""
        const pbrExportRequest = PbrExport.Config.exportRequest(
            PbrExport.Config.exportFolder([conversionRequest, PbrExport.Config.conversionInfoRequest(conversionRequest, "info", "text")], exportName),
            {source: "materialRevision"},
        )
        return this.createOutput(MaterialOutputType.PbrExportCustom, {pbrExportRequest})
    }

    renamePbrExport = async (materialOutput: MaterialOutputWithState) => {
        if (materialOutput.state != "Complete" || !materialOutput.result) {
            this.notifications.showInfo("Cannot rename PBR export without output data object")
            return
        }

        this.matDialog
            .open(RenameDialogComponent, {
                width: "400px",
                data: {
                    currentName: materialOutput.result.originalFileName.replace(".zip", ""),
                },
            })
            .afterClosed()
            .pipe(
                switchMap(async (filename) => {
                    if (!filename) {
                        return
                    }
                    await mutateThrowingErrors(this.updateMaterialOutputResultGql)({
                        input: {
                            id: materialOutput!.result!.id,
                            originalFileName: `${filename}.zip`,
                        },
                    })
                    this.notifications.showInfo("PBR export renamed")
                    this.refresh.item({id: this.$materialId(), __typename: ContentTypeModel.Material})
                }),
                catchError((err) => {
                    this.notifications.showInfo(`Failed to rename PBR export: ${err}`, undefined, "Dismiss")
                    return of()
                }),
            )
            .subscribe()
    }

    downloadOutputResult(materialOutput: MaterialOutputWithState) {
        if (materialOutput.result) {
            const anchor = document.createElement("a")
            anchor.href = materialOutput.result.downloadUrl
            anchor.click()
        }
    }

    // Computed button state signals
    readonly $pconExportButtonState: Signal<OutputButtonProps> = computed(() => {
        const materialOutput = this.$outputsWithState().find((materialOutput) => materialOutput.type === MaterialOutputType.PconExport)
        const sharedButtonProps: SharedOutputButtonProps = {
            buttonClass: "cm-button",
            icon: "fa-solid fa-p",
            label: "pCon export",
        }
        switch (materialOutput?.state) {
            case "Processing":
                return {
                    ...sharedButtonProps,
                    canCancel: false,
                    cancelTooltip: "Processing...",
                    state: "loading",
                }
            case "Init": // falls-through
            case "Runnable": // falls-through
            case "Running":
                return {
                    ...sharedButtonProps,
                    canCancel: true,
                    cancelTooltip: "Cancel job",
                    onCancel: () => this.cancelOutput(materialOutput),
                    state: "loading",
                }
            case "Cancelled":
                return {
                    ...sharedButtonProps,
                    onRemove: () => this.removeOutput(materialOutput),
                    onRetry: () => this.restartOutput(materialOutput),
                    removeTooltip: "Delete cancelled output",
                    retryTooltip: "Restart cancelled job",
                    state: "failed",
                }
            case "Failed":
                return {
                    ...sharedButtonProps,
                    onRemove: () => this.removeOutput(materialOutput),
                    onRetry: () => this.restartOutput(materialOutput),
                    removeTooltip: "Delete failed output",
                    retryTooltip: "Restart failed job",
                    state: "failed",
                }
            case "Complete": {
                // A non-owner viewing a publicly downloadable material may download a completed export
                // (the owner enabled public downloads) but not delete it.
                const canManage = this.$canGenerateOutputs()
                return {
                    ...sharedButtonProps,
                    canRemove: canManage,
                    downloadTooltip: "Download pCon planner export",
                    onDownload: () => this.downloadOutputResult(materialOutput),
                    onRemove: canManage ? () => this.removeOutput(materialOutput) : undefined,
                    removeTooltip: "Delete pCon planner export",
                    result: materialOutput.result,
                    state: "complete",
                }
            }
            default: {
                return {
                    ...sharedButtonProps,
                    createTooltip: "Generate pCon planner export",
                    onCreate: () => this.createOutput(MaterialOutputType.PconExport, {}),
                    state: "initial",
                }
            }
        }
    })

    readonly $fabricOverviewButtonState: Signal<OutputButtonProps> = computed(() => {
        const materialOutput = this.$outputsWithState().find((materialOutput) => materialOutput.type === MaterialOutputType.FabricOverview)
        const sharedButtonProps: SharedOutputButtonProps = {
            buttonClass: "cm-button",
            icon: "fa-regular fa-rug",
            label: "Fabric overview",
        }
        switch (materialOutput?.state) {
            case "Processing":
                return {
                    ...sharedButtonProps,
                    canCancel: false,
                    cancelTooltip: "Processing...",
                    state: "loading",
                }
            case "Init": // falls-through
            case "Runnable": // falls-through
            case "Running":
                return {
                    ...sharedButtonProps,
                    canCancel: true,
                    cancelTooltip: "Cancel job",
                    onCancel: () => this.cancelOutput(materialOutput),
                    state: "loading",
                }
            case "Cancelled":
                return {
                    ...sharedButtonProps,
                    canRemove: true,
                    onRemove: () => this.removeOutput(materialOutput),
                    onRetry: () => this.restartOutput(materialOutput),
                    removeTooltip: "Delete cancelled output",
                    retryTooltip: "Restart cancelled job",
                    state: "failed",
                }
            case "Failed":
                return {
                    ...sharedButtonProps,
                    canRemove: true,
                    onRemove: () => this.removeOutput(materialOutput),
                    onRetry: () => this.restartOutput(materialOutput),
                    removeTooltip: "Delete failed output",
                    retryTooltip: "Restart failed job",
                    state: "failed",
                }
            case "Complete": {
                return {
                    ...sharedButtonProps,
                    canRemove: true,
                    downloadTooltip: "Download fabric overview",
                    onDownload: () => this.downloadOutputResult(materialOutput),
                    onRemove: () => this.removeOutput(materialOutput),
                    removeTooltip: "Delete fabric overview",
                    result: materialOutput.result,
                    state: "complete",
                }
            }
            default: {
                return {
                    ...sharedButtonProps,
                    createTooltip: "Generate fabric overview",
                    onCreate: () => this.createOutput(MaterialOutputType.FabricOverview, {}),
                    state: "initial",
                }
            }
        }
    })

    readonly $customPbrExportsButtonStates: Signal<(OutputButtonProps & {exportSummary?: PbrExportSummary; sourceInfo?: string})[]> = computed(() => {
        return this.$customPbrExports().map((materialOutput) => {
            const sharedButtonProps: SharedOutputButtonProps & {exportSummary?: PbrExportSummary; sourceInfo?: string} = {
                buttonClass: "cm-text-button",
                label: this.$material()?.name ?? "Custom export",
                exportSummary: materialOutput?.config?.content
                    ? this.materialMapsExporterService.getSummaryForPbrExportConfigOrPbrExportRequest(materialOutput?.config?.content)
                    : undefined,
                sourceInfo: `Material revision ${materialOutput?.materialRevision?.number || materialOutput?.config?.content["sourceInfoRequest"]?.sourceId}`,
            }
            switch (materialOutput?.state) {
                case "Processing":
                    return {
                        ...sharedButtonProps,
                        canCancel: false,
                        cancelTooltip: "Processing...",
                        state: "loading",
                    }
                case "Init": // falls-through
                case "Runnable": // falls-through
                case "Running":
                    return {
                        ...sharedButtonProps,
                        canCancel: true,
                        cancelTooltip: "Cancel job",
                        onCancel: () => this.cancelOutput(materialOutput),
                        state: "loading",
                    }
                case "Cancelled":
                    return {
                        ...sharedButtonProps,
                        canRemove: this.$canDeletePbrExport(),
                        onRemove: () => this.removeOutput(materialOutput),
                        onRetry: () => this.restartOutput(materialOutput),
                        removeTooltip: "Delete cancelled output",
                        retryTooltip: "Restart cancelled job",
                        state: "failed",
                    }
                case "Failed":
                    return {
                        ...sharedButtonProps,
                        canRemove: this.$canDeletePbrExport(),
                        onRemove: () => this.removeOutput(materialOutput),
                        onRetry: () => this.restartOutput(materialOutput),
                        removeTooltip: "Delete failed output",
                        retryTooltip: "Restart failed job",
                        state: "failed",
                    }
                case "Complete": {
                    return {
                        ...sharedButtonProps,
                        canEdit: this.$can().update.material(null, "name"),
                        canRemove: this.$canDeletePbrExport(),
                        downloadTooltip: "Download PBR export",
                        editTooltip: "Rename PBR export",
                        hasUpdate:
                            this.$can().read.material(null, "sourceUpdates") &&
                            this.$material()?.latestCyclesRevision?.id !== materialOutput.materialRevision?.id,
                        onDownload: () => this.downloadOutputResult(materialOutput),
                        onEdit: () => this.renamePbrExport(materialOutput),
                        onRemove: () => this.removeOutput(materialOutput),
                        removeTooltip: "Delete PBR export",
                        state: "complete",
                        updateTooltip: "Source updates available",
                    }
                }
                default: {
                    // "initial" state does not exist for custom PBR exports
                    return {
                        ...sharedButtonProps,
                        createTooltip: "Processing...",
                        onCreate: () => {},
                        state: "initial",
                    }
                }
            }
        })
    })

    async createOutput(type: MaterialOutputType, settings: {pbrExportRequest?: PbrExport.Types.Request}) {
        const materialRevisionId = this.$material()?.latestCyclesRevision?.id
        if (materialRevisionId) {
            // update local state immediatly, disables the button
            const tempId = crypto.randomUUID()
            this.$localOutputsInProgress.update((outputs) => [
                ...outputs,
                {
                    id: tempId,
                    type,
                    outputCreated: false,
                },
            ])
            this.$outputsWithState.update((outputs) => [
                ...outputs,
                {
                    id: tempId,
                    __typename: "MaterialOutput",
                    type,
                    state: "Processing",
                },
            ])

            try {
                await this.materialOutputService.createOutput({
                    materialId: this.$materialId(),
                    materialRevisionId,
                    type,
                    showNotification: true,
                    ...settings,
                })

                // mark local output as ready to be replaced by the backend output
                this.$localOutputsInProgress.update((outputs) =>
                    outputs.map((output) => {
                        if (output.id === tempId) {
                            return {
                                ...output,
                                outputCreated: true,
                            }
                        }
                        return output
                    }),
                )

                // refresh
                await this.loadMaterialOutputs()
            } catch (error) {
                // remove the local "Processing" output so the spinner disappears
                this.$localOutputsInProgress.update((outputs) => outputs.filter((output) => output.id !== tempId))
                this.$outputsWithState.update((outputs) => outputs.filter((output) => output.id !== tempId))
                this.notifications.showError(error)
            }
        }
    }

    async cancelOutput(materialOutput: MaterialOutputWithState) {
        this.$outputsWithState.update((outputs) =>
            outputs.map((outputItem) => (outputItem.id === materialOutput.id ? {...outputItem, state: "Processing"} : outputItem)),
        )
        await this.materialOutputService.cancelAndRemoveOutput({
            materialOutput,
            showNotification: true,
        })
        await this.loadMaterialOutputs()
    }

    async restartOutput(materialOutput: MaterialOutputWithState) {
        this.$outputsWithState.update((outputs) =>
            outputs.map((outputItem) => (outputItem.id === materialOutput.id ? {...outputItem, state: "Processing"} : outputItem)),
        )
        await this.materialOutputService.restartOutput({
            materialOutput,
            showNotification: true,
        })
        await this.loadMaterialOutputs()
    }

    async removeOutput(materialOutput: MaterialOutputWithState) {
        const dialogRef = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Delete item",
                message: "Are you sure you want to remove the file?",
                confirmLabel: "Delete item",
                cancelLabel: "Cancel",
            },
        })

        dialogRef.afterClosed().subscribe(async (confirmed) => {
            if (!confirmed) {
                return
            }
            void this.cancelOutput(materialOutput)
        })
    }

    private isJobRunning(jobState: JobState) {
        return jobState === JobState.Runnable || jobState === JobState.Running || jobState === JobState.Init
    }
}
